import React from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Smile, X, RotateCw } from "lucide-react";
import { ALL_EMOJIS } from "@/lib/emojiSets";
import { EmojiTabsGrid } from "@/components/EmojiTabsGrid";

// Briques partagées du composeur de publication (feuille PC + plein écran téléphone).

export const EMOJIS = ALL_EMOJIS; // lot 634 — 3 catégories (lib/emojiSets)

export const EmojiGrid = ({ onEmoji, cell = "h-7 w-7 text-lg" }) => (
  <EmojiTabsGrid cols={8} cell={cell} onPick={onEmoji} gridTestId="emoji-grid" itemTestId={(e) => `emoji-${e}`} />
);

export const EmojiPicker = ({ onEmoji, testId = "composer-emoji-btn", label = "", className = "" }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        title={label || "Emoji"}
        data-testid={testId}
        className={className || "h-9 w-9 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)] transition-colors"}
      >
        <Smile size={21} className="text-amber-500" />
        {label && <span>{label}</span>}
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-64 p-2" align="end">
      <EmojiGrid onEmoji={onEmoji} />
    </PopoverContent>
  </Popover>
);

// Tiroir emoji (téléphone) : bandeau façon clavier, entre le corps et le pied — jamais sur « Publier ».
export const EmojiTray = ({ onEmoji, onClose, t, testId = "composer-emoji-tray" }) => (
  <div className="shrink-0 border-t px-3 pt-2 pb-2" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid={testId}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Emoji</span>
      <button type="button" onClick={onClose} className="h-7 w-7 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)]" aria-label={t("common.close", "Fermer")} data-testid={`${testId}-close`}>
        <X size={15} />
      </button>
    </div>
    <EmojiGrid onEmoji={onEmoji} cell="h-9 w-full text-2xl" />
  </div>
);

// État d'envoi (barre de progression façon Facebook sur l'aperçu).
export const UploadOverlay = ({ att, t, onRetry }) => {
  if (!att || att.status === "done") return null;
  if (att.status === "error") {
    return (
      <div className="absolute inset-0 z-10 bg-black/60 flex flex-col items-center justify-center gap-1.5" data-testid="composer-upload-error">
        <span className="text-white text-xs font-medium">{t("composer.upload_error", "Échec de l'envoi")}</span>
        <button
          type="button"
          onClick={onRetry}
          className="flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold"
          data-testid="composer-upload-retry"
        >
          <RotateCw size={12} /> {t("composer.retry", "Réessayer")}
        </button>
      </div>
    );
  }
  return (
    <div className="absolute inset-0 z-10 bg-black/45 grid place-items-center" data-testid="composer-upload-progress">
      <div className="w-2/3 max-w-[180px]">
        <div className="h-1.5 rounded-full bg-white/30 overflow-hidden">
          <div className="h-full bg-white rounded-full transition-[width] duration-200" style={{ width: `${att.progress}%` }} />
        </div>
        <div className="text-white text-[11px] text-center mt-1.5 font-medium">
          {att.progress < 100 ? `${att.progress}%` : t("composer.processing", "Traitement...")}
        </div>
      </div>
    </div>
  );
};

// Volume de la musique de fond : Doux · Moyen · Fort.
export const MusicVolumeButtons = ({ musicVolume, setMusicVolume, t, testId = "composer-music-volume" }) => (
  <div className="flex gap-1" data-testid={testId}>
    {[["soft", 0.35, "Doux"], ["medium", 0.7, "Moyen"], ["loud", 1.0, "Fort"]].map(([key, val, label]) => {
      const active = Math.abs(musicVolume - val) < 0.01;
      return (
        <button
          key={key}
          type="button"
          onClick={() => setMusicVolume(val)}
          className="rounded-full px-2.5 py-1 text-xs font-semibold border transition-colors"
          style={{ borderColor: active ? "var(--yalola-primary)" : "var(--yalola-border)", background: active ? "var(--yalola-primary)" : "transparent", color: active ? "#fff" : "inherit" }}
          data-testid={`${testId.replace(/-volume$/, "")}-vol-${key}`}
        >
          {t(`composer.music_vol_${key}`, label)}
        </button>
      );
    })}
  </div>
);
